import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAuth0 } from "@auth0/auth0-react";

import classes from "./Main.module.scss";
import { useRootDispatch, useRootSelector } from "store/hooks";
import { resetUser } from "store/user/user-slice";
import { useEffect, useRef, useState } from "react";
import LoadTranslation from "components/common/LoadTranslation";
import { clearAllNotifications } from "store/notification/notification-slice";
import { useAddNotification } from "hooks";
import {
  SESSION_EXPIRED_KEY,
  SESSION_EXPIRED_REASON_INACTIVE,
} from "constants/session";

interface MainProps {}

export const Main: React.FC<MainProps> = () => {
  const { isAuthenticated, isLoading, logout, loginWithRedirect } = useAuth0<{
    email_verified: boolean;
  }>();
  const navigate = useNavigate();
  const dispatch = useRootDispatch();
  const addNotification = useAddNotification();
  const { client, paymentCenter } = useRootSelector((state) => state.config);
  const [searchParams] = useSearchParams();
  const hasShownInactivityRef = useRef(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const { t } = useTranslation(["app"]);
  const logonEnabled =
    client.userRegistrationOption && client.userRegistrationOption !== 0;

  useEffect(() => {
    dispatch(clearAllNotifications());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const inactivityReason = sessionStorage.getItem(SESSION_EXPIRED_KEY);
    if (inactivityReason === SESSION_EXPIRED_REASON_INACTIVE) {
      setIsSessionExpired(true);
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    }
  }, []);

  useEffect(() => {
    if (isSessionExpired) {
      if (hasShownInactivityRef.current) {
        return;
      }
      hasShownInactivityRef.current = true;
      const notificationTimer = setTimeout(() => {
        addNotification({
          messages: [t("app:inactivity_logout")],
          type: "DEFAULT",
          closable: true,
        });
        setIsSessionExpired(false);
      }, 500);
      return () => clearTimeout(notificationTimer);
    }
  }, [isSessionExpired, addNotification, t]);

  useEffect(() => {
    if (isAuthenticated && !isSessionExpired) {
      navigate({
        pathname: `/${client.customerKey}/dashboard`,
        search: window.location.search,
      });
    }
    // eslint-disable-next-line
  }, [isAuthenticated, isSessionExpired]);

  if (searchParams.get("logout")) {
    dispatch(resetUser());
    logout();
  }

  const quickPayButtonHandler = () => {
    navigate(`/${client.customerKey}/quick-info`);
  };

  const loginButtonHandler = () => {
    // loginWithRedirect({
    //   authorizationParams: {
    //     redirect_uri: `${window.location.origin}/${customerKey}/dashboard`,
    //   },
    // });

    loginWithRedirect();
  };

  // Note: This could be an anonymous function inline the return of getLogon but I thought it was cleaner on its own
  const getFormButtons = () => {
    let logonButton: any;
    let registerButton: any;
    if (client.userRegistrationOption! >= 1) {
      logonButton = (
        <ft-button
          class={classes.Login}
          data-testid="login-button"
          fullwidth
          label={t("app:login_label")}
          unelevated
          onClick={loginButtonHandler}
        ></ft-button>
      );
    }

    if (client.userRegistrationOption === 1) {
      registerButton = (
        <ft-button
          data-testid="login-page-signup"
          class="outline-button"
          fullwidth
          label={t("app:create_account_label")}
          onClick={() =>
            loginWithRedirect({
              authorizationParams: { screen_hint: "signup" },
            })
          }
          outlined
        ></ft-button>
      );
    }

    return (
      <>
        {logonButton}
        {registerButton}
      </>
    );
  };
  const getLogonForm = () => {
    if (logonEnabled) {
      return (
        <section>
          <div className={classes.Form}>{getFormButtons()}</div>
          <div className={classes.Divider}>
            <div>
              <ft-divider></ft-divider>
            </div>
            {paymentCenter.quickPayments.enabled ? (
              <div className={classes.Divider_or}>{t("app:or")}</div>
            ) : (
              <></>
            )}
            <div>
              <ft-divider></ft-divider>
            </div>
          </div>
        </section>
      );
    } else {
      return <></>;
    }
  };

  const getQuickPayForm = () => {
    if (paymentCenter.quickPayments.deepLinkingOnly) {
      return <></>;
    } else {
      return (
        <section className={classes.QuickPay}>
          <h3 className={classes.QuickPay_heading}>
            {t("app:quick_pay_label")}
          </h3>
          <p role="document">{t("app:quick_pay_stop_check")}</p>
          <ft-button
            data-testid="login-page-quickpay"
            class="outline-button"
            fullwidth
            label={t("app:quick_pay_now_label")}
            unelevated
            onClick={quickPayButtonHandler}
          ></ft-button>
        </section>
      );
    }
  };

  const getDisclosureMessage = () => {
    return (
      <section className={classes.Disclosure}>
        <div className={classes.Disclosure_message}>
          {t("app:landing_page_disclosure")}
        </div>
      </section>
    );
  };

  return (
    <article className={classes.Container}>
      <div className={classes.spacer}></div>
      {getLogonForm()}
      {paymentCenter.quickPayments.enabled && getQuickPayForm()}
      {isLoading && logonEnabled ? (
        <ft-screen-loader></ft-screen-loader>
      ) : (
        <></>
      )}
      <LoadTranslation keys={["quickpay", "common"]} />
      <div className={classes.spacer}></div>
      {getDisclosureMessage()}
    </article>
  );
};
